import { IS_BROWSER } from 'config/appConfig'
import { getLocaleCodeFromUrl } from './localeUtils'
import { trackErrorInInstana } from './commonUtils'

/**
 * Lazy-load to avoid pulling view-layer SCSS into SSR
 * (Mysite → Common → Toast → styles.scss)
 */
const getMysiteContainer = () => require('src/models/Mysite').mysiteContainer
const getContentStack = () => require('src/api/contentStack').default

const DEFAULT_CONTEXT_NAME = 'csPageEntry'

/**
 * Convert Content Stack URL pattern to regex string
 * Handles placeholder syntax like /cft-demo/<cftId> and escapes special regex chars
 * @param {string} urlPattern - URL pattern from Content Stack (e.g., '/cft-demo/<cftId>')
 * @returns {string} - Regex pattern string (e.g., '^/cft-demo/[^/]+$')
 */
export function urlPatternToRegex(urlPattern) {
  try {
    if (typeof urlPattern !== 'string') {
      return ''
    }
    let regexPattern = urlPattern

    // Escape special regex chars first, before converting placeholders
    // This ensures we can add the '+' quantifier in the placeholder regex without it being escaped
    regexPattern = regexPattern
      .replace(/\./g, '\\.')
      .replace(/\(/g, '\\(')
      .replace(/\)/g, '\\)')
      .replace(/\+/g, '\\+')
      .replace(/\?/g, '\\?')
      .replace(/\*/g, '\\*')
      .replace(/\|/g, '\\|')

    // Now convert placeholder syntax <...> to regex quantifier [^/]+
    regexPattern = regexPattern.replace(/<[^>]+>/g, '[^/]+')

    // Add anchors if not already present
    if (!regexPattern.startsWith('^')) {
      regexPattern = '^' + regexPattern
    }
    if (!regexPattern.endsWith('$')) {
      regexPattern = regexPattern + '$'
    }

    return regexPattern
  } catch (e) {
    console.error('Error converting URL pattern to regex:', e)
    return ''
  }
}

/**
 * Check if a URL path matches a Content Stack URL pattern
 * @param {string} pathName - URL path to check (e.g., '/cft-demo/123')
 * @param {string} entryUrl - Content Stack URL pattern (e.g., '/cft-demo/<cftId>')
 * @returns {boolean} - Does the path match?
 */
export function isPathMatchingContentStackUrl(pathName, entryUrl) {
  try {
    // Exact match first
    if (entryUrl === pathName) {
      return true
    }

    // Convert pattern to regex and test
    const regexPattern = urlPatternToRegex(entryUrl)
    if (!regexPattern) {
      return false
    }

    const urlRegex = new RegExp(regexPattern)
    return urlRegex.test(pathName)
  } catch (e) {
    console.error('Error matching path to Content Stack URL:', e)
    return false
  }
}

/**
 * Generates a regex pattern for Content Stack query based on the current path.
 * Used to fetch relevant entries including potential placeholders.
 * @param {string} pathName - Current URL path
 * @returns {string} - Regex pattern for API query
 */
export function getCsRegexPatternForPath(pathName) {
  if (!pathName || pathName === '/') return '^/$'

  // Match only the first segment after the initial slash (e.g., '/foo/bar/baz' => '^/foo/[^/]+')
  const match = pathName.match(/^\/([^/]+)(.*)$/)
  if (match) {
    const firstSegment = match[1]
    const restPath = match[2]
    if (!restPath) {
      return `^/${firstSegment}$`
    }
    return `^/${firstSegment}/[^/]+$`
  }
  return `^${pathName}$`
}

export function getTransformedCSPageData(response, currentUrl) {
  let transformsPage = {}
  try {
    // Lazy-load to avoid SSR build issues with static imports of transform module.
    const {
      transformCSPageEntries,
    } = require('src/models/ContentStack/transform')
    transformsPage = transformCSPageEntries?.({ response, currentUrl }) || {}
  } catch (e) {
    transformsPage = {}
  }

  return {
    url: currentUrl,
    entries: transformsPage,
    // True when a URL-specific CS entry (not just /default) matched this URL.
    // Used by NotFound to distinguish "real CS page" from "default MFE only".
    hasActualEntry: transformsPage?.hasActualEntry === true,
    isLoading: false,
  }
}

export function getContentStackEntries({ contextPageEntry, contentStackPage }) {
  if (IS_BROWSER) {
    return contentStackPage?.entries || contextPageEntry || null
  }
  return contextPageEntry?.entries || contentStackPage?.entries || null
}

export async function getMySiteDefaultCategoriesFromCS() {
  const mysite = getMysiteContainer()
  const showDefaultCategories =
    mysite?.mysiteShopHeaderResponse?.market?.showDefaultCategories === true
  if (!showDefaultCategories) {
    mysite.setMysiteDefaultCategoriesFromCS([])
    return []
  }

  try {
    const locale =
      getLocaleCodeFromUrl()?.split('_')?.reverse()?.join('-') || 'US-en'
    const csData = await getContentStack().getEntry(
      'nextgen_mysite_category',
      locale
    )

    /* Storefront ContentStack API returns snake_case keys */
    const rawCategories =
      csData?.default_category_and_products ??
      csData?.defaultCategoryAndProducts ??
      []

    const normalized = (Array.isArray(rawCategories) ? rawCategories : []).map(
      (category, index) => {
        const skusArr = Array.isArray(category?.skus) ? category.skus : []
        const skuIds = skusArr
          .map(skuItem => {
            const sku =
              skuItem?.productid_skuid ??
              skuItem?.productidSkuid ??
              skuItem?.globalproductid ??
              ''
            return sku ? String(sku) : ''
          })
          .filter(Boolean)

        const categoryName =
          category?.category_name ?? category?.categoryName ?? ''
        const categoryDescription =
          category?.category_description ?? category?.categoryDescription ?? ''

        const id = `cs-default-${index}`
        return {
          id,
          categoryId: id,
          name: categoryName,
          categoryName,
          description: categoryDescription,
          categoryDescription,
          skuIds,
        }
      }
    )

    mysite.setMysiteDefaultCategoriesFromCS(normalized)
    return normalized
  } catch (error) {
    trackErrorInInstana({
      errorReport: 'Error fetching mysite default categories from Contentstack',
      errorData: error,
    })
    mysite.setMysiteDefaultCategoriesFromCS([])
    return []
  }
}

export function getDefaultCategoriesFromCSMap() {
  const mysite = getMysiteContainer()
  const csCategories = mysite?.mysiteDefaultCategoriesFromCS || []
  const defaultCategoryInfo = mysite?.mysiteDefaultCategoryInfo || {}

  return csCategories.reduce((acc, category) => {
    if (!category) return acc
    const id = category.id ?? category.categoryId ?? null
    if (id === null || id === undefined) return acc

    const skuIds = Array.isArray(category.skuIds)
      ? category.skuIds.filter(Boolean)
      : []
    const name =
      category.categoryName ||
      category.name ||
      defaultCategoryInfo.categoryName ||
      ''
    const description =
      category.categoryDescription ||
      category.description ||
      defaultCategoryInfo.categoryDescription ||
      ''

    acc[id] = {
      id,
      categoryId: id,
      name,
      categoryName: name,
      description,
      categoryDescription: description,
      skuIds,
      skuCount: skuIds.length,
      skus: skuIds.map(sku => ({ sku })),
    }
    return acc
  }, {})
}
